import * as THREE from 'three';
import * as CANNON from 'cannon';
import { IUpdatable } from '../interfaces/IUpdatable';
import { BoxCollider } from './colliders/BoxCollider';
import { TrimeshCollider } from './colliders/TrimeshCollider';
import { CollisionGroups } from '../enums/CollisionGroups';

interface ManagedBoxCollider {
	position: CANNON.Vec3;
	size: THREE.Vector3;
	quaternion: CANNON.Quaternion;
	body: CANNON.Body | null;
}

interface ManagedTrimeshCollider {
	mesh: THREE.Object3D;
	body: CANNON.Body | null;
}

export class DynamicCollisionSystem implements IUpdatable
{
	public updateOrder = 0;

	private static readonly ACTIVATION_DIST_SQ = 20 * 20;
	private static readonly DEACTIVATION_DIST_SQ = 25 * 25;

	private boxColliders: ManagedBoxCollider[] = [];
	private trimeshColliders: ManagedTrimeshCollider[] = [];

	constructor(
		private physicsWorld: CANNON.World,
		private getCharacterPositions: () => THREE.Vector3[]
	) {}

	public registerCollider(position: CANNON.Vec3, size: THREE.Vector3, quaternion: CANNON.Quaternion): void
	{
		this.boxColliders.push({ position, size, quaternion, body: null });
	}

	public registerTrimeshCollider(mesh: THREE.Object3D): void
	{
		this.trimeshColliders.push({ mesh, body: null });
	}

	public update(timeStep: number, unscaledTimeStep: number): void
	{
		const positions = this.getCharacterPositions();
		if (positions.length === 0) { return; }

		for (const entry of this.boxColliders)
		{
			const anyClose = positions.some((p) => this.distSq(entry.position, p) < DynamicCollisionSystem.ACTIVATION_DIST_SQ);
			const allFar   = positions.every((p) => this.distSq(entry.position, p) > DynamicCollisionSystem.DEACTIVATION_DIST_SQ);

			if (entry.body === null && anyClose)      { this.activateBox(entry); }
			else if (entry.body !== null && allFar)   { this.deactivateEntry(entry); }
		}

		for (const entry of this.trimeshColliders)
		{
			const anyClose = positions.some((p) => this.distSq(entry.mesh.position, p) < DynamicCollisionSystem.ACTIVATION_DIST_SQ);
			const allFar   = positions.every((p) => this.distSq(entry.mesh.position, p) > DynamicCollisionSystem.DEACTIVATION_DIST_SQ);

			if (entry.body === null && anyClose)      { this.activateTrimesh(entry); }
			else if (entry.body !== null && allFar)   { this.deactivateEntry(entry); }
		}
	}

	private activateBox(entry: ManagedBoxCollider): void
	{
		const phys = new BoxCollider({ size: entry.size });
		phys.body.position.copy(entry.position);
		phys.body.quaternion.copy(entry.quaternion);
		phys.body.computeAABB();

		phys.body.shapes.forEach((shape) => {
			shape.collisionFilterMask = ~CollisionGroups.TrimeshColliders;
		});

		this.physicsWorld.addBody(phys.body);
		entry.body = phys.body;
	}

	private activateTrimesh(entry: ManagedTrimeshCollider): void
	{
		const phys = new TrimeshCollider(entry.mesh, {});
		this.physicsWorld.addBody(phys.body);
		entry.body = phys.body;
	}

	private deactivateEntry(entry: ManagedBoxCollider | ManagedTrimeshCollider): void
	{
		this.physicsWorld.remove(entry.body);
		entry.body = null;
	}

	private distSq(a: { x: number; y: number; z: number }, b: THREE.Vector3): number
	{
		const dx = a.x - b.x;
		const dy = a.y - b.y;
		const dz = a.z - b.z;
		return dx * dx + dy * dy + dz * dz;
	}
}
